'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { getBrowsingContextTracker } from '@/lib/browsing-context-tracker';
import { HyperRealismBanner, type PresenceBannerMessage } from './hyper-realism-banner';
import { createClient } from '@/lib/supabase/client';
import { SENTIENT_MODE_LOCKED } from '@/lib/sentient-mode-lock';

export function HyperRealismProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [currentMessage, setCurrentMessage] = useState<PresenceBannerMessage | null>(null);

  useEffect(() => {
    // Locked: don't record page visits for a feature that can't deliver.
    if (SENTIENT_MODE_LOCKED) return;
    // Update browsing context when pathname changes
    const tracker = getBrowsingContextTracker();
    tracker.updateCurrentPage(pathname);
  }, [pathname]);

  useEffect(() => {
    // Locked: no polling, no realtime channel -- nothing will ever be sent.
    if (SENTIENT_MODE_LOCKED) return;
    pollForMessages();

    // Poll for new messages every 30 seconds
    const interval = setInterval(pollForMessages, 30000);

    // Subscribe to real-time messages
    const supabase = createClient();
    const channel = supabase
      .channel('hyper-realism-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'hyper_realism_messages',
        },
        (payload: { new: { id: string; dismissed_at: string | null } }) => {
          if (!payload.new.dismissed_at) {
            fetchMessageDetails(payload.new.id);
          }
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      channel.unsubscribe();
    };
  }, []);

  const fetchMessageDetails = async (messageId: string) => {
    try {
      const response = await fetch(`/api/hyper-realism/messages?undelivered=true`);
      const data = await response.json();

      if (data.messages && data.messages.length > 0) {
        const message = data.messages.find((m: PresenceBannerMessage) => m.id === messageId);
        if (message) {
          setCurrentMessage(message);
        }
      }
    } catch (error) {
      console.error('Failed to fetch message details:', error);
    }
  };

  const pollForMessages = async () => {
    // No client-side "is this feature on" gate here — eligibility (per-
    // companion presence_profile.reliance config + the user's own opt-out
    // via /api/companions/[id]/proactive-messages) is enforced entirely
    // server-side by the presence scheduler cron (lib/companion-presence-
    // scheduler.ts, /api/cron/presence). This just polls for whatever real
    // undelivered messages exist — if the user isn't eligible for any,
    // that's always an empty array, so there's nothing to gate client-side.
    // (Previously gated on usage_quotas.hyper_realism_enabled, a flag from
    // the old, unrelated, dead hyper-realism-service.ts system that nothing
    // ever set to true — which silently blocked this from ever firing.)
    try {
      const response = await fetch('/api/hyper-realism/messages?undelivered=true');
      const data = await response.json();

      if (data.messages && data.messages.length > 0) {
        // Show the most recent undelivered message
        setCurrentMessage(data.messages[0]);
      }
    } catch (error) {
      console.error('Failed to poll for messages:', error);
    }
  };

  const handleDismiss = async () => {
    if (!currentMessage) return;

    try {
      await fetch(`/api/hyper-realism/messages/${currentMessage.id}/dismiss`, {
        method: 'POST',
      });
      setCurrentMessage(null);
    } catch (error) {
      console.error('Failed to dismiss message:', error);
    }
  };

  const handleStop = async (messageId: string, companionId: string) => {
    try {
      await Promise.all([
        fetch(`/api/hyper-realism/messages/${messageId}/dismiss`, { method: 'POST' }),
        fetch(`/api/companions/${companionId}/proactive-messages`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: false }),
        }),
      ]);
      setCurrentMessage(null);
    } catch (error) {
      console.error('Failed to stop proactive messages:', error);
    }
  };

  return (
    <>
      {currentMessage && (
        <HyperRealismBanner
          message={currentMessage}
          onDismiss={handleDismiss}
          onStop={handleStop}
        />
      )}
      {children}
    </>
  );
}
